import React, { useEffect, useRef, useState, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import io from "socket.io-client";
import { AuthContext } from "../context/AuthContext";
import api from "../utils/api";
import DoctorSidebar from "./DoctorSidebar";
import "./DoctorVideoCall.css";

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || "https://turemedicine-backend-1.onrender.com";
const socket = io(SOCKET_URL, {
  reconnection: true,
  reconnectionAttempts: 20,
  reconnectionDelay: 1000,
  transports: ["websocket"],
  autoConnect: false,
  path: "/socket.io",
  query: { userType: "doctor" }, // or "doctor" in DoctorVideoCall.js
  timeout: 60000,
  withCredentials: true,
});

const DoctorVideoCall = () => {
  const { appointmentId } = useParams();
  const { doctorUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const [appointment, setAppointment] = useState(null);
  const [localStream, setLocalStream] = useState(null);
  const [remoteStream, setRemoteStream] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [callActive, setCallActive] = useState(false);
  const [callEnded, setCallEnded] = useState(false);
  const [callDuration, setCallDuration] = useState(null);
  const [endMessage, setEndMessage] = useState("");
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [consultationStarted, setConsultationStarted] = useState(false);
  const [isInitiator, setIsInitiator] = useState(false);
  const [isNegotiating, setIsNegotiating] = useState(false);
  const [isPrescriptionLoading, setIsPrescriptionLoading] = useState(false);

  const myVideo = useRef(null);
  const patientVideo = useRef(null);
  const peerConnection = useRef(null);
  const startTime = useRef(null);

  useEffect(() => {
    if (!appointmentId) return;

    socket.io.opts.query.appointmentId = appointmentId;
    socket.connect();
    console.log("Doctor connecting to Socket.IO at:", SOCKET_URL);

    socket.on("connect", () => {
      console.log("Doctor Socket.IO connected with ID:", socket.id);
      socket.emit("join consultation", { appointmentId, userType: "doctor" });
    });

    socket.on("connect_error", (err) => {
      console.error("Doctor Socket.IO connect error:", err);
      setError(
        "Unable to connect to the video call server. Please ensure you’ve trusted the certificate by visiting " +
        `<a href="https://192.168.33.136:5000" target="_blank">https://192.168.33.136:5000</a>.`
      );
    });

    socket.on("disconnect", (reason) => {
      console.log("Doctor Socket.IO disconnected:", reason);
    });

    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);

    const fetchAppointment = async () => {
      if (!doctorUser || doctorUser.role !== "doctor") {
        setError("You must be logged in as a doctor.");
        setIsLoading(false);
        return;
      }
      try {
        const response = await api.get(`/appointments/${appointmentId}`, {
          headers: { Authorization: `Bearer ${doctorUser.accessToken}` },
        });
        console.log("Doctor fetched appointment:", response.data);
        setAppointment(response.data);
      } catch (err) {
        setError("Failed to load appointment: " + (err.response?.data?.message || err.message));
      } finally {
        setIsLoading(false);
      }
    };
    fetchAppointment();

    return () => {
      window.removeEventListener("resize", handleResize);
      socket.off("connect");
      socket.off("connect_error");
      socket.off("disconnect");
      cleanup();
    };
  }, [appointmentId, doctorUser]);

  const initializePeerConnection = async () => {
    if (peerConnection.current) {
      peerConnection.current.close();
      console.log("Doctor closed existing peer connection");
    }

    let mediaStream;
    try {
      mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: true,
      });
      setLocalStream(mediaStream);
      startTime.current = new Date();
      console.log("Doctor obtained local media stream");
    } catch (err) {
      console.error("Media error:", err);
      setError(`Failed to access camera/microphone: ${err.name} - ${err.message}`);
      return;
    }

    peerConnection.current = new RTCPeerConnection({
      iceServers: [
        { urls: "stun:stun.l.google.com:19302" },
        { urls: "stun:stun1.l.google.com:19302" },
        { urls: "turn:openrelay.metered.ca:80", username: "openrelayproject", credential: "openrelayproject" },
      ],
    });

    mediaStream.getTracks().forEach((track) => {
      peerConnection.current.addTrack(track, mediaStream);
      console.log("Doctor added track:", track.kind);
    });

    peerConnection.current.ontrack = (event) => {
      console.log("Doctor received remote track:", event.streams[0]);
      setRemoteStream(event.streams[0]);
      setCallActive(true);
    };

    peerConnection.current.onicecandidate = (event) => {
      if (event.candidate) {
        socket.emit("ice-candidate", { appointmentId, candidate: event.candidate });
        console.log("Doctor sent ICE candidate:", event.candidate.candidate);
      }
    };

    peerConnection.current.onconnectionstatechange = () => {
      if (!peerConnection.current) return;
      console.log("Doctor connection state:", peerConnection.current.connectionState);
      if (peerConnection.current.connectionState === "failed") {
        setError("Connection failed. Please try again.");
        cleanup();
      }
    };

    peerConnection.current.onsignalingstatechange = () => {
      if (!peerConnection.current) return;
      console.log("Doctor signaling state:", peerConnection.current.signalingState);
      if (peerConnection.current.signalingState === "stable" && !callActive) {
        setIsNegotiating(false);
      }
    };
  };

  const sendOffer = async () => {
    if (!peerConnection.current) {
      console.log("Doctor initializing peer connection before sending offer");
      await initializePeerConnection();
    }
    if (peerConnection.current && !isNegotiating && peerConnection.current.signalingState === "stable") {
      try {
        setIsNegotiating(true);
        const offer = await peerConnection.current.createOffer();
        await peerConnection.current.setLocalDescription(offer);
        socket.emit("offer", { appointmentId, offer });
        console.log("Doctor sent offer:", offer.sdp.substring(0, 50) + "...");
        setIsNegotiating(false);
      } catch (err) {
        console.error("Doctor failed to send offer:", err);
        setIsNegotiating(false);
      }
    } else {
      console.log("Doctor cannot send offer: state=", peerConnection.current?.signalingState, "negotiating=", isNegotiating);
    }
  };

  useEffect(() => {
    if (!appointment || isLoading || error || !doctorUser) return;

    const setupWebRTC = async () => {
      await initializePeerConnection();
    };
    setupWebRTC();

    socket.on("start negotiation", ({ role }) => {
      console.log(`Doctor role set to: ${role}`);
      setIsInitiator(role === "initiator");
      if (role === "initiator" && consultationStarted) {
        console.log("Doctor is initiator, sending offer");
        sendOffer();
      }
    });

    socket.on("user joined", ({ userType }) => {
      if (userType === "patient" && consultationStarted) {
        console.log("Patient joined, doctor initiator status:", isInitiator);
        if (isInitiator) {
          console.log("Doctor sending offer after patient join");
          sendOffer();
        }
      }
    });

    socket.on("offer", async ({ offer }) => {
      if (!isInitiator && peerConnection.current && !isNegotiating) {
        try {
          setIsNegotiating(true);
          await peerConnection.current.setRemoteDescription(new RTCSessionDescription(offer));
          const answer = await peerConnection.current.createAnswer();
          await peerConnection.current.setLocalDescription(answer);
          socket.emit("answer", { appointmentId, answer });
          console.log("Doctor sent answer:", answer.sdp.substring(0, 50) + "...");
          setIsNegotiating(false);
        } catch (err) {
          console.error("Doctor failed to handle offer:", err);
          setIsNegotiating(false);
        }
      }
    });

    socket.on("answer", async ({ answer }) => {
      if (isInitiator && peerConnection.current && !isNegotiating) {
        try {
          setIsNegotiating(true);
          await peerConnection.current.setRemoteDescription(new RTCSessionDescription(answer));
          console.log("Doctor set patient answer successfully");
          setIsNegotiating(false);
        } catch (err) {
          console.error("Doctor failed to set answer:", err);
          setIsNegotiating(false);
        }
      }
    });

    socket.on("ice-candidate", async (candidate) => {
      if (peerConnection.current && peerConnection.current.remoteDescription) {
        try {
          await peerConnection.current.addIceCandidate(new RTCIceCandidate(candidate));
          console.log("Doctor added ICE candidate from patient");
        } catch (err) {
          console.error("Doctor failed to add ICE candidate:", err);
        }
      }
    });

    socket.on("user disconnected", ({ userType }) => {
      if (userType === "patient") {
        setCallActive(false);
        console.log("Patient disconnected, resetting call state");
      }
    });

    socket.on("call ended", ({ duration, endedBy }) => {
      setCallActive(false);
      setCallEnded(true);
      setCallDuration(duration);
      setEndMessage(endedBy === "patient" ? "Patient has ended the call" : "Call ended");
      cleanup();
      console.log("Doctor call ended with duration:", duration, "ended by:", endedBy);
    });

    return () => {
      socket.off("start negotiation");
      socket.off("user joined");
      socket.off("offer");
      socket.off("answer");
      socket.off("ice-candidate");
      socket.off("user disconnected");
      socket.off("call ended");
    };
  }, [appointment, isLoading, error, appointmentId, doctorUser, consultationStarted]);

  useEffect(() => {
    if (myVideo.current) {
      myVideo.current.srcObject = localStream;
      if (localStream) {
        myVideo.current.play().catch((err) => console.error("Doctor local video play error:", err));
      }
    }
  }, [localStream]);

  useEffect(() => {
    if (patientVideo.current) {
      patientVideo.current.srcObject = remoteStream;
      if (remoteStream) {
        patientVideo.current.play().catch((err) => console.error("Doctor remote video play error:", err));
      }
    }
  }, [remoteStream]);

  const startConsultation = async () => {
    if (!doctorUser || !appointment) {
      setError("Cannot start consultation: Missing doctor or appointment data.");
      return;
    }
    const doctorId = doctorUser.id;
    const patientId = appointment.patientId?._id || appointment.patientId;
    try {
      const response = await api.post(
        "/start-consultation",
        { doctorId, patientId, appointmentId },
        { headers: { Authorization: `Bearer ${doctorUser.accessToken}` } }
      );
      console.log("Consultation started:", response.data);
      setConsultationStarted(true);
      setIsInitiator(true);
    } catch (err) {
      setError("Failed to start consultation: " + (err.response?.data?.message || err.message));
    }
  };

  const cleanup = () => {
    if (peerConnection.current) {
      peerConnection.current.ontrack = null;
      peerConnection.current.onicecandidate = null;
      peerConnection.current.onconnectionstatechange = null;
      peerConnection.current.onsignalingstatechange = null;
      peerConnection.current.close();
      peerConnection.current = null;
      console.log("Doctor peer connection closed and listeners removed");
    }
    if (localStream) {
      localStream.getTracks().forEach((track) => track.stop());
      setLocalStream(null);
      console.log("Doctor stream stopped");
    }
    if (patientVideo.current) patientVideo.current.srcObject = null;
    setRemoteStream(null);
    setCallActive(false);
    setIsNegotiating(false);
  };

  const handleEndCall = async () => {
    const endTime = new Date();
    const durationMs = startTime.current ? endTime - startTime.current : 0;
    const duration = `${Math.floor(durationMs / 60000)}m ${Math.floor((durationMs % 60000) / 1000)}s`;

    setCallActive(false);
    setCallEnded(true);
    setCallDuration(duration);
    setEndMessage("You have ended the call");
    socket.emit("end call", { appointmentId, duration, endedBy: "doctor" });

    try {
      await api.put(
        `/appointments/${appointmentId}/complete`,
        { callDuration: duration, completedAt: endTime, prescriptionStatus: "Pending" },
        { headers: { Authorization: `Bearer ${doctorUser.accessToken}` } }
      );
      console.log("Doctor marked appointment as complete");
    } catch (err) {
      setError("Failed to end consultation: " + (err.response?.data?.message || err.message));
    } finally {
      cleanup();
    }
  };

  const handlePrescriptionChoice = async (choice) => {
    if (choice === "now") {
      setIsPrescriptionLoading(true);
    }
    try {
      const prescriptionStatus = choice === "now" ? "Pending" : "Pending";
      await api.put(
        `/appointments/${appointmentId}/complete`,
        {
          callDuration,
          completedAt: new Date(),
          prescriptionStatus,
          status: "completed",
        },
        { headers: { Authorization: `Bearer ${doctorUser.accessToken}` } }
      );
      console.log("Doctor marked appointment as complete");
      navigate(choice === "now" ? `/doctor/prescription/${appointmentId}` : `/doctor-dashboard/${doctorUser.id}`, {
        replace: true,
      });
    } catch (err) {
      setError("Failed to update appointment status: " + (err.response?.data?.message || err.message));
    } finally {
      if (choice === "now") {
        setIsPrescriptionLoading(false);
      }
    }
  };

  if (isLoading) return <div className="dvc-loading">Loading...</div>;
  if (error)
    return (
      <div className="dvc-container">
        <DoctorSidebar isMobile={isMobile} />
        <main className="dvc-main">
          <h1 className="dvc-title">Error</h1>
          <p className="dvc-text" dangerouslySetInnerHTML={{ __html: error }}></p>
          <button className="dvc-button" onClick={() => navigate("/doctor-dashboard")}>
            Back
          </button>
        </main>
      </div>
    );

  const patientName = appointment?.patientId?.name || "Unknown Patient";

  return (
    <div className="dvc-container">
      <DoctorSidebar isMobile={isMobile} />
      <main className="dvc-main">
        <h1 className="dvc-title">Video Consultation</h1>
        {callEnded ? (
          <div className="dvc-call-ended">
            <h2 className="dvc-subtitle">Call Ended</h2>
            <p className="dvc-text">{endMessage}</p>
            {callDuration && <p className="dvc-text">Duration: {callDuration}</p>}
            <button
              className={`dvc-button ${isPrescriptionLoading ? "dvc-button-loading" : ""}`}
              onClick={() => handlePrescriptionChoice("now")}
              disabled={isPrescriptionLoading}
            >
              {isPrescriptionLoading ? (
                <span className="dvc-spinner"></span>
              ) : (
                "Make Prescription Now"
              )}
            </button>
            <button
              className="dvc-button dvc-secondary"
              onClick={() => handlePrescriptionChoice("later")}
            >
              Make it Later
            </button>
          </div>
        ) : (
          <>
            {!consultationStarted && (
              <button className="dvc-button" onClick={startConsultation}>
                Start Consultation
              </button>
            )}
            <div className="dvc-video-container">
              <div className="dvc-video-wrapper">
                <p className="dvc-video-label">Your Video</p>
                <video ref={myVideo} autoPlay muted playsInline className="dvc-video" style={{ width: "300px" }} />
              </div>
              <div className="dvc-video-wrapper">
                <p className="dvc-video-label">{`${patientName}'s Video`}</p>
                <video ref={patientVideo} autoPlay playsInline className="dvc-video" style={{ width: "300px" }} />
              </div>
            </div>
            <p className="dvc-status-text">
              {callActive ? "Patient is on the line" : "Waiting for patient to join"}
            </p>
            {callActive && (
              <button className="dvc-button" onClick={handleEndCall}>
                End Consultation
              </button>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default DoctorVideoCall;